import { inject, injectable } from "tsyringe";
import { WINDOW } from "@identity/symbols";
import {
  Action,
  CommunicationService,
  eventsRepository,
} from "@identity/services";
import { FANDOM_CONTEXT_UPDATE } from "@identity/custom_events";
import { FandomContextCreator } from "./fandom-context-creator";
import { createCmpRestrictedObject } from "@identity/utils";

@injectable()
export class FandomContextService {
  constructor(
    @inject(WINDOW) private readonly window: Window,
    @inject(CommunicationService)
    private readonly communicationService: CommunicationService,
    @inject(FandomContextCreator)
    private readonly fandomContextCreator: FandomContextCreator,
  ) {}

  /*
   * Extends `window.fandomContext` set on the server side
   * to guarantee compliance with the `Window` type
   */
  public init() {
    const tracking = this.fandomContextCreator.createTrackingContext();
    const targeting = this.fandomContextCreator.createTargetingContext();
    const { blockthrough, anyclip } =
      this.fandomContextCreator.createPartnersContext();

    // Certain properties must remain unavailable until tracking is allowed
    // to not break implementations that previously depended on them being undefined
    this.window.fandomContext = {
      ...this.window.fandomContext,
      site: { ...(this.window.fandomContext?.site ?? { tags: {} }) },
      page: { ...(this.window.fandomContext?.page ?? { tags: {} }) },
      tracking: createCmpRestrictedObject(tracking),
      targeting: createCmpRestrictedObject(targeting),
      partners: {
        blockthrough: createCmpRestrictedObject(blockthrough),
        anyclip: createCmpRestrictedObject(anyclip),
      },
    };

    this.communicationService.emit(
      new Action(eventsRepository.FANDOM_CONTEXT_SET),
    );

    const event = new CustomEvent(FANDOM_CONTEXT_UPDATE);
    this.window.dispatchEvent(event);
  }

  public setNextPageViewData() {
    const newPageViewData =
      this.fandomContextCreator.createPageViewTrackingContext();
    Object.assign(this.window.fandomContext.tracking, newPageViewData);

    const event = new CustomEvent(FANDOM_CONTEXT_UPDATE);
    this.window.dispatchEvent(event);
  }
}
